import { useEffect, useState } from 'react'
import {
  AuthorizationLoginRequired,
  requestAuthorization,
} from './mcp-authorization-api'
import { McpAuthorizationForm } from './mcp-authorization-form'
import type { AuthorizationRequest } from './mcp-authorization-api'
import type { McpScope } from '@amitable/shared/mcp-contract'
import { LoginPage } from '@/pages/login/login-page'

export function McpAuthorizationPage() {
  const [request, setRequest] = useState<AuthorizationRequest | null>(null)
  const [loginRequired, setLoginRequired] = useState(false)
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [oauthQuery] = useState(() => window.location.search.slice(1))

  useEffect(() => {
    const controller = new AbortController()
    void requestAuthorization<AuthorizationRequest>(
      'request',
      { oauthQuery },
      controller.signal,
    )
      .then(setRequest)
      .catch((failure: unknown) => {
        if (controller.signal.aborted) return
        if (failure instanceof AuthorizationLoginRequired)
          setLoginRequired(true)
        else
          setError(
            'This authorization request is unavailable or expired. Restart the connection from the app.',
          )
      })

    return () => controller.abort()
  }, [oauthQuery])

  async function decide(
    accept: boolean,
    workspaceId: string,
    scopes: Array<McpScope>,
  ) {
    if (!request || pending) return
    setPending(true)
    setError('')
    try {
      const result = await requestAuthorization<{ url: string }>('decision', {
        oauthQuery,
        accept,
        accountId: request.account.id,
        workspaceId,
        scopes,
      })
      window.location.assign(result.url)
    } catch (failure) {
      if (failure instanceof AuthorizationLoginRequired) setLoginRequired(true)
      else
        setError(
          'Authorization could not complete. Restart the connection from the app.',
        )
      setPending(false)
    }
  }

  if (loginRequired) return <LoginPage />

  return (
    <main className="min-h-screen bg-background px-6 py-12 text-foreground">
      <section
        className="mx-auto max-w-xl space-y-6 rounded-xl border bg-card p-6 shadow-sm"
        aria-label="Authorize an MCP app"
      >
        <h1 className="text-2xl font-semibold">Connect an app to Amitable</h1>
        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
        {!request && !error ? <p>Loading authorization request...</p> : null}
        {request ? (
          <McpAuthorizationForm
            request={request}
            pending={pending}
            onDecision={(accept, workspaceId, scopes) =>
              void decide(accept, workspaceId, scopes)
            }
          />
        ) : null}
      </section>
    </main>
  )
}
